import React from 'react';
import { Card } from '../ui/card';
import { ShoppingBag, User, Mail, Phone, MapPin, MessageCircle, Calendar } from 'lucide-react';

const currencySymbol = (currency) => {
  const code = (currency || '').toLowerCase();
  if (code === 'usd') return '$';
  if (code === 'eur') return '€';
  return '₦';
};

const formatAddress = (address) => {
  if (!address) return null;
  const parts = [
    address.line1,
    address.line2,
    [address.city, address.state, address.postal_code].filter(Boolean).join(', '),
    address.country,
  ].filter(Boolean);
  return parts.join(', ');
};

const InfoRow = ({ icon: Icon, label, value, testId }) => {
  if (!value) return null;
  return (
    <div className="flex items-start gap-2 text-sm" data-testid={testId}>
      <Icon className="h-4 w-4 text-gray-400 mt-0.5 flex-shrink-0" />
      <div className="flex-1">
        <span className="text-gray-500 font-medium">{label}: </span>
        <span className="text-gray-800">{value}</span>
      </div>
    </div>
  );
};

const OrdersList = ({ orders }) => (
  <Card className="p-6">
    <h3 className="text-lg font-bold text-gray-900 mb-6">Book Orders</h3>
    {orders.length === 0 ? (
      <div className="text-center py-12">
        <ShoppingBag className="h-16 w-16 text-gray-300 mx-auto mb-4" />
        <p className="text-gray-500">No orders yet</p>
        <p className="text-sm text-gray-400 mt-2">
          Orders will appear here when customers purchase books
        </p>
      </div>
    ) : (
      <div className="space-y-4">
        {orders.map((order) => {
          const isPhysical = order.book_type === 'physical';
          const orderDate = order.created_at
            ? new Date(order.created_at).toLocaleString()
            : null;
          return (
            <div
              key={order.id}
              className="border border-gray-200 p-5 rounded-lg hover:shadow-md transition-shadow"
              data-testid={`order-card-${order.id}`}
            >
              {/* Header row: book + price */}
              <div className="flex justify-between items-start gap-4 mb-3 pb-3 border-b border-gray-100">
                <div className="flex-1">
                  <p className="font-bold text-gray-900 text-base">
                    {order.book_name}
                    {order.quantity > 1 && (
                      <span className="ml-2 text-sm text-indigo-600 font-semibold">
                        × {order.quantity}
                      </span>
                    )}
                  </p>
                  <p className="text-xs text-gray-500 mt-1 capitalize">
                    {order.book_type || 'physical'} · {order.payment_status || 'paid'}
                  </p>
                </div>
                <p className="text-lg font-bold text-green-600 whitespace-nowrap">
                  {currencySymbol(order.currency)}
                  {Number(order.amount || 0).toFixed(2)}
                </p>
              </div>

              {/* Customer details */}
              <div className="space-y-2">
                <InfoRow
                  icon={User}
                  label="Customer"
                  value={order.customer_name}
                  testId={`order-${order.id}-name`}
                />
                <InfoRow
                  icon={Mail}
                  label="Email"
                  value={order.customer_email}
                  testId={`order-${order.id}-email`}
                />
                <InfoRow
                  icon={Phone}
                  label="Phone"
                  value={order.customer_phone}
                  testId={`order-${order.id}-phone`}
                />
                {isPhysical && order.shipping_address && (
                  <>
                    <InfoRow
                      icon={MapPin}
                      label="Ship to"
                      value={
                        <>
                          {order.shipping_name && (
                            <span className="font-semibold">{order.shipping_name}<br /></span>
                          )}
                          {formatAddress(order.shipping_address)}
                        </>
                      }
                      testId={`order-${order.id}-shipping`}
                    />
                    <InfoRow
                      icon={MessageCircle}
                      label="WhatsApp"
                      value={order.whatsapp}
                      testId={`order-${order.id}-whatsapp`}
                    />
                  </>
                )}
                {isPhysical && !order.shipping_address && (
                  <p className="text-xs text-amber-600 italic flex items-center gap-1">
                    <MapPin className="h-3 w-3" />
                    No shipping address on record for this physical order.
                  </p>
                )}
                {orderDate && (
                  <InfoRow
                    icon={Calendar}
                    label="Placed"
                    value={orderDate}
                    testId={`order-${order.id}-date`}
                  />
                )}
              </div>
            </div>
          );
        })}
      </div>
    )}
  </Card>
);

export default OrdersList;
